import { useFindBy } from "@gadgetinc/react";
import { Icon } from "@shopify/polaris";
import { message } from "antd";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import "chart.js/auto";
import { Datepicker } from "flowbite-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import { FallbackPage } from "../../externalComponents/FallbackPage";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import StatisticsCards from "../../externalComponents/StatisticsCards";
import { Tooltip } from "../../externalComponents/Tooltip";
import { BarChartComponent } from "../../externalComponents/analytics/BarChartComponent";
import { ButtonsList } from "../../externalComponents/analytics/ButtonList";
import { PieChartComponent } from "../../externalComponents/analytics/PieChartComponent";
import { RecipientsList } from "../../externalComponents/analytics/RecipientsList";
import { SalesList } from "../../externalComponents/analytics/SalesList";
import { SendingListFuture } from "../../externalComponents/analytics/SendingListFuture";
import { SendingListPast } from "../../externalComponents/analytics/SendingListPast";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Checkbox,
  CheckboxField,
  CheckboxGroup,
} from "../../externalComponents/tailwind/checkbox";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import {
  Field,
  Fieldset,
  Label,
  Legend as LegendTailwind,
} from "../../externalComponents/tailwind/fieldset";
import SelectInput from "../../externalComponents/tailwind/select-input";
import { Text } from "../../externalComponents/tailwind/text";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { fetchCampaignAnalyticsStats } from "../../functions/fetchCampaignAnalyticsStats";
import { getDateRange } from "../../functions/getDateRange";
import { CustomerContext } from "../../providers";
import { getAutomationTypeIcon } from "../automations/helpers/functions";

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
);

export default function AnalyticsCampaignPage() {
  const navigate = useNavigate();
  const { user, customer, shop, integrations } = useContext(CustomerContext);

  const location = useLocation();
  const campaignId = location.pathname.split("/").slice(-1)[0];
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [addStats, setAddStats] = useState(true);
  const [addContactDetails, setAddContactDetails] = useState(false);
  const [addCustomFields, setAddCustomFields] = useState(false);
  const [addButtonsClicks, setAddButtonsClicks] = useState(false);
  const [exportByMail, setExportByMail] = useState(false);

  const [selectedDateFilter, setSelectedDateFilter] = useState("allData");
  const [selectedDateFilterExport, setSelectedDateFilterExport] =
    useState("allData");
  const [dataStats, setDataStats] = useState(null);
  const [timeFilterSuffix, setTimeFilterSuffix] = useState("allData");
  const [isLoading, setIsLoading] = useState(true);
  const today = new Date();
  const todayAt23h59 = new Date();
  todayAt23h59.setHours(23, 59, 59, 999);
  const [startDate, setStartDate] = useState(() => {
    const newDate = new Date(today);
    newDate.setDate(newDate.getDate() - 30);
    return newDate;
  });
  const [endDate, setEndDate] = useState(todayAt23h59);

  const [startDateExport, setStartDateExport] = useState(startDate);
  const [endDateExport, setEndDateExport] = useState(endDate);
  const [failedMessagesData, setFailedMessagesData] = useState(null);
  const [fetchingFailedMessages, setFetchingFailedMessages] = useState(false);

  const isMobile = useIsMobile();

  const handleSelectChange = useCallback((value) => {
    setSelectedDateFilter(value);
    setSelectedDateFilterExport(value);
    setTimeFilterSuffix(value);
  }, []);

  const handleSelectExportChange = useCallback((value) => {
    setSelectedDateFilterExport(value);
  }, []);

  const options = [
    { label: "Today", value: "Today" },
    { label: "This week", value: "ThisWeek" },
    { label: "This month", value: "ThisMonth" },
    { label: "Last 7 days", value: "7days" },
    { label: "Last month", value: "LastMonth" },
    { label: "Last 30 days", value: "30days" },
    { label: "Last 90 days", value: "90days" },
    { label: "This year", value: "ThisYear" },
    { label: "Last year", value: "LastYear" },
    { label: "Over all time", value: "allData" },
    { label: "Between", value: "Between" },
  ];

  const optionsFormat = [
    { label: "xlsx", value: "xlsx" },
    { label: "csv", value: "csv" },
  ];

  const [{ data: campaignData, fetching }] = useFindBy(
    api.campaign.findById,
    campaignId,
    {
      filter: [
        {
          trigger: { equals: "manual" },
        },
        {
          isArchived: { notEquals: true },
        },
      ],
      select: {
        id: true,
        name: true,
        updatedAt: true,
        createdAt: true,
        trigger: true,
        automationType: true,
        scenario: { id: true, type: true },
      },
      sort: {
        updatedAt: "Descending",
      },
    },
  );

  // Fetch campaign stats using views (streaming)
  useEffect(() => {
    if (!campaignData) return;

    let isCancelled = false;
    setDataStats(null);
    setIsLoading(true);

    // For allData, use campaign createdAt as start date
    const effectiveStartDate =
      selectedDateFilter === "allData"
        ? new Date(campaignData.createdAt)
        : startDate;

    fetchCampaignAnalyticsStats({
      customerId: customer.id,
      shopId: shop?.id,
      campaignId: campaignId,
      selectedDateFilter,
      startDate: effectiveStartDate,
      endDate: endDate,
      scenarioType: campaignData?.scenario?.type,
      onUpdate: (updater) => {
        if (!isCancelled) {
          setDataStats((prev) => updater(prev || {}));
          setIsLoading(false);
        }
      },
    });

    return () => {
      isCancelled = true;
    };
  }, [
    selectedDateFilter,
    startDate,
    endDate,
    customer.id,
    shop?.id,
    campaignId,
    campaignData,
  ]);

  const calculateRate = (numerator, denominator) => {
    return Math.min(1, numerator / Math.max(1, denominator)) * 100;
  };

  const sentRate = dataStats
    ? calculateRate(
        dataStats[`sent${timeFilterSuffix}`],
        campaignData?.scenario?.type == "campaign"
          ? dataStats[`messagesCountAllTime`]
          : dataStats[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const deliveredRate = dataStats
    ? calculateRate(
        dataStats[`delivered${timeFilterSuffix}`],
        campaignData?.scenario?.type == "campaign"
          ? dataStats[`sentAllTime`]
          : dataStats[`sent${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const openRate = dataStats
    ? calculateRate(
        dataStats[`read${timeFilterSuffix}`],
        campaignData?.scenario?.type == "campaign"
          ? dataStats[`deliveredAllTime`]
          : dataStats[`delivered${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const failedRate = dataStats
    ? calculateRate(
        dataStats[`messagesCount${timeFilterSuffix}`] -
          dataStats[`sent${timeFilterSuffix}`],
        campaignData?.scenario?.type == "campaign"
          ? dataStats[`messagesCountAllTime`]
          : dataStats[`messagesCount${timeFilterSuffix}`],
      ).toFixed(0)
    : null;

  const conversionRate = (
    Math.min(
      1,
      dataStats?.[`sales${timeFilterSuffix}`] /
        Math.max(1, dataStats?.[`sent${timeFilterSuffix}`]),
    ) * 100
  )?.toFixed(1);

  const optoutRate = (
    Math.min(
      1,
      dataStats?.[`optoutCount${timeFilterSuffix}`] /
        Math.max(1, dataStats?.[`messagesCount${timeFilterSuffix}`]),
    ) * 100
  )?.toFixed(1);

  const turnover = !dataStats
    ? null
    : dataStats?.[`turnover${timeFilterSuffix}`]
      ? dataStats?.[`turnover${timeFilterSuffix}`].toFixed(0)
      : dataStats?.[`externalTurnover${timeFilterSuffix}`]
        ? dataStats?.[`externalTurnover${timeFilterSuffix}`].toFixed(0)
        : 0;

  const stats = [
    {
      name: "Sales",
      tooltip: true,
      tooltipContent: "Number of sales attributed to WhatsApp.",
      value: !dataStats
        ? null
        : dataStats?.[`sales${timeFilterSuffix}`] > 0
          ? dataStats?.[`sales${timeFilterSuffix}`]
          : "0",
    },
    {
      name: "Conversion rate",
      tooltip: true,
      tooltipContent: "Percentage of messages that resulted in a sale.",
      value: !dataStats
        ? null
        : dataStats?.[`sales${timeFilterSuffix}`] > 0
          ? conversionRate + "%"
          : "-",
      change: !dataStats
        ? null
        : `${dataStats?.[`sales${timeFilterSuffix}`] || "0"} / ${dataStats?.[`sent${timeFilterSuffix}`] || "0"}`,
    },
    {
      name: "Turnover",
      tooltip: true,
      tooltipContent: "The total revenue attributed to WhatsApp.",
      value: turnover === null ? null : `${turnover || 0}€`,
    },
    {
      name: "ARPC",
      tooltip: true,
      tooltipContent:
        "Average Revenue Per Conversation: turnover divided by sent messages.",
      value: !dataStats
        ? null
        : dataStats?.[`turnover${timeFilterSuffix}`] &&
            dataStats?.[`sent${timeFilterSuffix}`]
          ? (
              dataStats?.[`turnover${timeFilterSuffix}`] /
              dataStats?.[`sent${timeFilterSuffix}`]
            ).toFixed(2) + "€"
          : dataStats?.[`externalTurnover${timeFilterSuffix}`] > 0
            ? (
                dataStats?.[`externalTurnover${timeFilterSuffix}`] /
                dataStats?.[`delivered${timeFilterSuffix}`]
              ).toFixed(2) + "€"
            : "0€",
      change: !dataStats
        ? null
        : `${
            dataStats?.[`turnover${timeFilterSuffix}`]
              ? dataStats?.[`turnover${timeFilterSuffix}`].toFixed(0) + "€"
              : dataStats?.[`externalTurnover${timeFilterSuffix}`]
                ? dataStats?.[`externalTurnover${timeFilterSuffix}`].toFixed(
                    0,
                  ) + "€"
                : "0€"
          } / ${dataStats?.[`sent${timeFilterSuffix}`] || "0"} sent`,
    },

    {
      name: "Cost",
      tooltip: true,
      tooltipContent:
        "The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !dataStats
        ? null
        : `${(dataStats?.[`totalWhatsappCostInEuros${timeFilterSuffix}`] || 0).toFixed(2)}€`,
    },
    {
      name: "ROI",
      tooltip: true,
      tooltipContent:
        "Turnover divided by WhatsApp cost. The WhatsApp cost is available for messages sent after May 17th, 2025.",
      value: !dataStats
        ? null
        : dataStats?.[`totalWhatsappCostInEuros${timeFilterSuffix}`]
          ? `${(turnover / (dataStats?.[`totalWhatsappCostInEuros${timeFilterSuffix}`] || 0)).toFixed(2)}`
          : "-",
    },
  ];

  useEffect(() => {
    if (!campaignData || !customer.id) {
      return;
    }

    const fetchFailedMessages = async () => {
      setFetchingFailedMessages(true);
      try {
        const dateRange = getDateRange(selectedDateFilter, startDate, endDate);
        const effectiveStartDate =
          selectedDateFilter === "allData"
            ? new Date(campaignData.createdAt)
            : dateRange.startDate
              ? new Date(dateRange.startDate)
              : startDate;
        const effectiveEndDate = dateRange.endDate
          ? new Date(dateRange.endDate)
          : endDate;

        const result = await api.view(
          `($customer: String, $campaignId:String, $from: DateTime, $to: DateTime) {
            messages {
              failedReason
              bucket: count(1)
              [where customerId == $customer && campaignId == $campaignId && (isTest == false || isTest== null) && failedAt != null && createdAt >= $from && createdAt <= $to group by failedReason]
            }
          }
        `,
          {
            customer: customer.id,
            campaignId: campaignId,
            from: effectiveStartDate,
            to: effectiveEndDate,
          },
        );

        setFailedMessagesData(result?.messages || []);
      } catch (error) {
        console.error("Error fetching failed messages:", error);
        setFailedMessagesData([]);
      } finally {
        setFetchingFailedMessages(false);
      }
    };

    fetchFailedMessages();
  }, [
    selectedDateFilter,
    startDate,
    endDate,
    customer.id,
    campaignId,
    campaignData,
  ]);

  const failedMessagesCount =
    dataStats?.[`messagesCount${timeFilterSuffix}`] -
      dataStats?.[`sent${timeFilterSuffix}`] || 0;

  const statsRate = [
    {
      name: "Sent rate",
      value: !dataStats ? null : sentRate > 0 ? sentRate + "%" : "-",
      change: !dataStats
        ? null
        : campaignData?.scenario?.type == "campaign"
          ? `${dataStats?.[`sent${timeFilterSuffix}`] || 0}/${
              dataStats?.[`messagesCountAllTime`] || 0
            }`
          : `${dataStats?.[`sent${timeFilterSuffix}`] || 0}/${
              dataStats?.[`messagesCount${timeFilterSuffix}`] || 0
            }`,
    },
    {
      name: "Delivered rate",
      value: !dataStats ? null : deliveredRate > 0 ? deliveredRate + "%" : "-",
      change: !dataStats
        ? null
        : campaignData?.scenario?.type == "campaign"
          ? `${dataStats?.[`delivered${timeFilterSuffix}`] || 0}/${
              dataStats?.[`sentAllTime`] || 0
            }`
          : `${dataStats?.[`delivered${timeFilterSuffix}`] || 0}/${
              dataStats?.[`sent${timeFilterSuffix}`] || 0
            }`,
    },
    {
      name: "Open rate",
      value: !dataStats ? null : openRate > 0 ? openRate + "%" : "-",
      change: !dataStats
        ? null
        : campaignData?.scenario?.type == "campaign"
          ? `${dataStats?.[`read${timeFilterSuffix}`] || 0}/${
              dataStats?.[`deliveredAllTime`] || 0
            }`
          : `${dataStats?.[`read${timeFilterSuffix}`] || 0}/${
              dataStats?.[`delivered${timeFilterSuffix}`] || 0
            }`,
    },
    {
      name: "Failed rate",
      value: !dataStats ? null : failedRate > 0 ? failedRate + "%" : "-",
      change: !dataStats
        ? null
        : `${failedMessagesCount}/${dataStats?.[`messagesCount${timeFilterSuffix}`] || 0}`,
    },
  ];

  const metrics = [
    {
      name: "Opt-out rate",
      value: !dataStats ? null : optoutRate > 0 ? optoutRate + "%" : "-",
      change: !dataStats
        ? null
        : `${dataStats?.[`optoutCount${timeFilterSuffix}`] || "0"}/${
            dataStats?.[`messagesCount${timeFilterSuffix}`] || "0"
          }`,
    },
  ];

  const automationTypeIcon = getAutomationTypeIcon(
    campaignData?.automationType,
  );

  const exportStatistics = async () => {
    try {
      if (exportByMail) {
        setIsExportLoading(true);

        await api.enqueue(
          api.exportCampaignStatsByMail,
          {
            customerId: customer.id,
            campaignId: campaignId,
            scenarioId: campaignData?.scenario.id,
            addStats: addStats,
            addContactDetails: addContactDetails,
            addCustomFields: addCustomFields,
            addButtonsClicks: addButtonsClicks,
            dateFilter: selectedDateFilterExport,
            startDate: startDateExport,
            endDate: endDateExport,
            userEmail: user.email,
          },
          {
            queue: {
              name: `export-campaign-stats-by-mail-${customer.id}`,
              maxConcurrency: 1,
            },
          },
        );

        message.success("Export will be sent to your email");
        setIsModalExportOpen(false);
      } else {
        message.loading("Exporting your campaign statistics...");
        setIsExportLoading(true);

        var response = await api.exportCampaignStats({
          customerId: customer.id,
          campaignId: campaignId,
          scenarioId: campaignData?.scenario.id,
          exportFormat: exportFormat,
          addStats: addStats,
          addContactDetails: addContactDetails,
          addCustomFields: addCustomFields,
          addButtonsClicks: addButtonsClicks,
          dateFilter: selectedDateFilterExport,
          startDate: startDateExport,
          endDate: endDateExport,
        });

        if (!response || !response.fileContent) {
          throw new Error("Failed to download the file.");
        }

        console.log("API Response:", response);
        console.log("File Content:", response.fileContent);
        console.log("Content Type:", response.contentType);
        console.log("File Name:", response.fileName);

        const uint8Array = new Uint8Array(response.fileContent.data);
        const blob = new Blob([uint8Array], { type: response.contentType });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download =
          response.fileName || `exported-statistics.${exportFormat}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsModalExportOpen(false);
      }
    } catch (error) {
      console.error("Error exporting statistics:", error);
      message.error(
        "Failed to export campaign statistics. Please try again later.",
      );
    }
    setIsExportLoading(false);
  };

  const filterDates = useMemo(
    () => getDateRange(selectedDateFilter, startDate, endDate),
    [selectedDateFilter, startDate, endDate],
  );

  const customTheme = {
    popup: {
      root: {
        base: isMobile
          ? "fixed left-1/2 top-1/2 z-90 block pt-2 w-[90vw] max-w-xs -translate-x-1/2 -translate-y-1/2"
          : "absolute top-10 z-50 block pt-2 w-[22em]",
        inner:
          "inline-block rounded-customBorderRadius bg-white p-4 border w-full mb-5 dark:bg-gray-800",
        input: {
          field: {
            input: {
              base: "hover:border-yellow ring-purple focus:ring-transparent",
            },
          },
        },
      },
    },
    views: {
      days: {
        items: {
          base: "grid w-full grid-cols-7",
          item: {
            base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
            selected: "bg-yellow hover:bg-yellow",
            disabled: "text-gray-400",
          },
        },
      },
    },
  };

  const handleDateChange = (date, type, isExport) => {
    if (type === "start") {
      if (isExport) {
        setStartDateExport(date);
      } else {
        setStartDate(date);
        setStartDateExport(date);
      }
    } else if (type === "end") {
      if (isExport) {
        setEndDateExport(date);
      } else {
        const adjustedEndDate = new Date(date);
        adjustedEndDate.setHours(23, 59, 59, 999);
        setEndDate(adjustedEndDate);
        setEndDateExport(adjustedEndDate);
      }
    }
  };

  return fetching ? (
    <>
      <LoadingSpinner />
    </>
  ) : !campaignData ? (
    <FallbackPage />
  ) : (
    <>
      <PageHeading
        title={
          <>
            <div className="flex">
              {campaignData?.automationType && (
                <Icon
                  source={automationTypeIcon}
                  color="base"
                  accessibilityLabel="Status"
                />
              )}
              <div className="ml-2">{campaignData?.name}</div>
            </div>
          </>
        }
        {...(!isMobile && {
          dropdownButton: true,
          dropdownItems: [
            ...(!customer.shouldHidePersonalData || user.id === "2021"
              ? [
                  {
                    label: "Export data",
                    onClick: () => setIsModalExportOpen(true),
                  },
                ]
              : []),
            {
              label: "Edit",
              onClick: () => {
                navigate(
                  campaignData?.automationType === null
                    ? `/campaigns/edit/${campaignId}`
                    : `/automations/edit/${campaignId}`,
                );
              },
            },
            ...(campaignData?.automationType === null
              ? [
                  {
                    label: "Schedule",
                    onClick: () => {
                      navigate(`/campaigns/send/${campaignId}`);
                    },
                  },
                ]
              : []),
          ],
        })}
        select={true}
        isSelectComponentsDisabled={isLoading}
        selectComponents={{
          options: options,
          selected: selectedDateFilter,
          onChange: (value) => handleSelectChange(value),
          value: selectedDateFilter,
        }}
        actionsBelow={isMobile}
      />
      <OverflowFrame>
        {selectedDateFilter === "Between" && (
          <div className="flex mt-4 mx-3 md:mx-0">
            <div className="w-[22em]">
              <Datepicker
                theme={customTheme}
                maxDate={today}
                value={startDate}
                onChange={(date) => {
                  handleDateChange(date, "start", false);
                }}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
                disabled={isLoading}
              />
            </div>
            <div className="w-[22em] ml-3">
              <Datepicker
                theme={customTheme}
                maxDate={today}
                minDate={startDate}
                value={endDate}
                onChange={(date) => {
                  handleDateChange(date, "end", false);
                }}
                weekStart={1}
                showClearButton={false}
                showTodayButton={false}
                disabled={isLoading}
              />
            </div>
          </div>
        )}
        {(integrations.shopify || integrations.splio) && (
          <div className="border-2 rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
            <Text className="text-xl font-bold mb-5">Revenue</Text>
            <div className="md:flex items-start justify-between">
              <div className="md:w-1/2 md:mr-3">
                <StatisticsCards stats={stats} isSplit={true} />
              </div>

              <div className="border rounded-customBorderRadius p-3 md:w-1/2 md:ml-3 mt-3 md:mt-0">
                <Text className="text-lg font-bold">Attributed sales</Text>
                <SalesList
                  campaign={campaignId}
                  shop={shop}
                  user={user}
                  filterDates={filterDates}
                />
              </div>
            </div>
          </div>
        )}
        <div className="border border-pink rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
          <Text className="text-xl font-bold mb-5">
            {`Deliverability on `}
            <span className="text-[#DF0F63] text-xl">
              {dataStats?.[`messagesCount${timeFilterSuffix}`] || "0"}
            </span>
            {` targeted recipients`}
            {!!dataStats?.[`totalWhatsappCostInEuros${timeFilterSuffix}`] && (
              <>
                {" "}
                for{" "}
                <span className="text-[#DF0F63] text-xl">
                  {dataStats?.[
                    `totalWhatsappCostInEuros${timeFilterSuffix}`
                  ].toFixed(2) + "€"}
                </span>
              </>
            )}
          </Text>

          <StatisticsCards stats={statsRate} />
          <div className="md:flex mt-3">
            <div className="flex flex-col w-full md:w-[50%] mr-3">
              <div className="border rounded-customBorderRadius p-3">
                <BarChartComponent
                  dataStats={dataStats}
                  timeFilterSuffix={timeFilterSuffix}
                  selectedDateFilter={selectedDateFilter}
                  isMobile={isMobile}
                />
              </div>
              {!isMobile && (
                <div className="flex-col border rounded-customBorderRadius p-3 mt-3">
                  <Text className="text-lg font-bold">Scheduled messages</Text>
                  <SendingListFuture
                    campaign={campaignId}
                    integrations={integrations}
                  />
                  <div className="flex flex-col justify-between grow">
                    <Text className="text-lg font-bold">Past messages</Text>
                    <SendingListPast
                      campaign={campaignId}
                      filterDates={filterDates}
                      integrations={integrations}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="w-full md:w-[50%] flex flex-col mt-3 md:mt-0">
              <div className="border rounded-customBorderRadius p-3">
                <PieChartComponent
                  isMobile={isMobile}
                  failedMessagesCount={failedMessagesCount}
                  failedMessagesData={failedMessagesData}
                  fetchingFailedMessages={fetchingFailedMessages}
                />
              </div>
              <div className="flex-col justify-between border  rounded-customBorderRadius p-3 mt-3">
                <div className="flex flex-col justify-between grow">
                  <Text className="text-lg font-bold">Last messages</Text>
                  <RecipientsList
                    campaign={campaignId}
                    filterDates={filterDates}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-2 rounded-customBorderRadius p-3 my-4 mx-3 md:mx-0">
          <Text className="text-xl font-bold mb-5">Engagement</Text>
          <div className="mb-2">
            <StatisticsCards stats={metrics} />
          </div>
          {campaignData && (
            <ButtonsList
              scenarioId={campaignData?.scenario.id}
              campaignId={campaignId}
              user={user}
              timeFilterSuffix={timeFilterSuffix}
              filterDates={filterDates}
              dataStats={dataStats}
            />
          )}
        </div>
      </OverflowFrame>

      <Dialog
        key="dialog_export"
        open={isModalExportOpen}
        onClose={() => setIsModalExportOpen(false)}
      >
        <DialogTitle>Export campaign statistics</DialogTitle>

        <DialogBody className="flex-1 ">
          <Field className="flex w-full">
            <div className="flex mb-2 min-w-[100%] items-center">
              <LegendTailwind>Select export format</LegendTailwind>
              <div className="min-w-[90px] ml-3">
                <SelectInput
                  value={exportFormat}
                  onChange={(value) => {
                    setExportFormat(value);
                    if (value === "csv") {
                      setAddStats(false);
                    }
                  }}
                  options={optionsFormat}
                  selected={exportFormat}
                />
              </div>
            </div>
          </Field>
          <Field>
            <div className="flex mb-2 min-w-[100%] items-center">
              <LegendTailwind>Select data period</LegendTailwind>
              <div className="min-w-[90px] ml-3 w-[11em]">
                <SelectInput
                  value={selectedDateFilterExport}
                  onChange={(value) => handleSelectExportChange(value)}
                  options={options}
                  selected={selectedDateFilterExport}
                />
              </div>
            </div>
          </Field>
          {selectedDateFilterExport === "Between" && (
            <div className="flex my-2">
              <div className="w-[22em]">
                <Datepicker
                  theme={customTheme}
                  maxDate={today}
                  value={startDateExport}
                  onChange={(date) => handleDateChange(date, "start", true)}
                  weekStart={1}
                  showClearButton={false}
                  showTodayButton={false}
                />
              </div>
              <div className="w-[22em] ml-3">
                <Datepicker
                  theme={customTheme}
                  maxDate={today}
                  minDate={startDateExport}
                  value={endDateExport}
                  onChange={(date) => handleDateChange(date, "end", true)}
                  weekStart={1}
                  showClearButton={false}
                  showTodayButton={false}
                />
              </div>
            </div>
          )}

          <Fieldset>
            <CheckboxGroup>
              <CheckboxField>
                {exportFormat === "xlsx" && (
                  <>
                    <Checkbox
                      name="statsSheet"
                      checked={addStats}
                      onChange={() => {
                        setAddStats(!addStats);
                      }}
                    />
                    <Label>Add statistics sheet</Label>
                  </>
                )}
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="contactDetails"
                  checked={addContactDetails}
                  onChange={() => {
                    setAddContactDetails(!addContactDetails);
                  }}
                />
                <Label>Add contact details</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="custom fields"
                  checked={addCustomFields}
                  onChange={() => {
                    setAddCustomFields(!addCustomFields);
                  }}
                />
                <Label>Add custom fields</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox
                  name="buttons clicks"
                  checked={addButtonsClicks}
                  onChange={() => {
                    setAddButtonsClicks(!addButtonsClicks);
                  }}
                />
                <Label>Add buttons clicks</Label>
              </CheckboxField>
               <div className="flex gap-1 pt-5">
              <CheckboxField>
                <Checkbox
                  name="exportByMail"
                  checked={exportByMail}
                  onChange={() => {
                    setExportByMail(!exportByMail);
                  }}
                />
                <Label>Export by mail</Label>
              </CheckboxField>
                <Tooltip
                  size="4"
                  content={<div>Recommended for large datasets to avoid timeouts</div>}
                />
              </div>
            </CheckboxGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => {
              setIsModalExportOpen(false);
              setSelectedDateFilterExport(selectedDateFilter);
            }}
            disabled={isExportLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={() => exportStatistics()}
            className="min-w-[100px] h-10"
            loading={isExportLoading}
          >
            {exportByMail ? "Send export by email" : "Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
